import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Upload, Link2, AlertTriangle, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, showUploadError, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIER_LABELS = {
  discover_boost: "Discover Boost",
  boost: "Boost",
  pro_boost: "Pro Boost",
};
const VIDEO_LABELS = {
  pro_boost: "Home Screen Video",
  boost: "Leaderboard & Top Spot Video",
  discover_boost: "Leaderboard & Top Spot Video",
};

const money = (cents, currency) =>
  cents == null ? "—" : `${(cents / 100).toFixed(2)} ${(currency || "usd").toUpperCase()}`;

export default function BoostConfirm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const submissionId = new URLSearchParams(search).get("submission");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sub, setSub] = useState(null);
  const [option, setOption] = useState(null);
  const [socialLink, setSocialLink] = useState("");
  const [socialLink2, setSocialLink2] = useState("");
  const [listingId, setListingId] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const videoInputRef = useRef(null);

  const loadSubmission = useCallback(async () => {
    setLoading(true);
    try {
      if (sessionId) {
        const { data } = await api.get(`/boost/payment/${sessionId}`);
        setSub(data);
      } else {
        const { data } = await api.get("/boost/my-paid-submissions");
        const found = submissionId ? data.items.find((s) => s.id === submissionId) : data.items[0];
        if (!found) setError("We couldn't find a paid submission waiting for content on this account.");
        setSub(found || null);
      }
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId, submissionId]);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/auth", { state: { from: `/boost/confirm${search}` } });
      return;
    }
    loadSubmission();
  }, [user, navigate, search, loadSubmission]);

  useEffect(() => {
    if (sub && sub.match_status === "unmatched") {
      api.get("/companies/mine")
        .then((r) => setMyListings(r.data ? [r.data] : []))
        .catch(() => setMyListings([]));
    }
  }, [sub]);

  const needsVideo = sub && (sub.tier === "boost" || sub.tier === "pro_boost" || (sub.tier === "discover_boost" && option === "option_b"));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sub.tier === "discover_boost" && !option) return toast.error("Choose Option A or Option B");
    const videoOnly = sub.tier === "discover_boost" && option === "option_b";
    if (needsVideo && !videoFile && !sub.video_url)
      return toast.error("A video is required for this tier");
    if (videoOnly && !videoFile) return toast.error("Please upload a video for this option");
    if (!videoOnly && !socialLink.trim() && !videoFile)
      return toast.error("Add a social post link or upload a video");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("social_link", socialLink.trim());
      if (socialLink2.trim()) fd.append("social_link_2", socialLink2.trim());
      if (option) fd.append("option_selected", option);
      if (listingId) fd.append("listing_id", listingId);
      if (videoFile) fd.append("video_file", videoFile);
      await api.post(`/boost/submissions/${sub.id}/content`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
    } catch (err) {
      showUploadError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 pt-28 pb-24">
      <section className="max-w-2xl mx-auto px-5">

        {/* Loading */}
        {loading && (
          <div data-testid="boost-confirm-loading" className="flex items-center justify-center gap-3 text-slate-500 text-sm pt-20">
            <Loader2 size={18} className="animate-spin text-emerald-500" /> Confirming your payment…
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div data-testid="boost-confirm-error" className="rounded-2xl border border-red-200 bg-red-50 p-8">
            <AlertTriangle size={26} className="text-red-500 mb-4" />
            <h1 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-2">Something's off</h1>
            <p className="text-sm text-slate-600 mb-6">{error}</p>
            <Link to="/leaderboard" className="text-sm font-bold text-emerald-600 hover:text-emerald-500 transition">
              Back to leaderboard
            </Link>
          </div>
        )}

        {/* Legacy content form submitted */}
        {!loading && !error && sub && done && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="boost-content-submitted-panel"
            className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-3">Content in review</h1>
            <p className="text-sm text-slate-600 max-w-md mx-auto mb-8 leading-relaxed">
              Your {TIER_LABELS[sub.tier]} content for <span className="text-slate-900 font-semibold">{sub.business_name}</span> is
              with our team. We review within 24 hours and email you the moment it goes live.
            </p>
            <Link
              to="/leaderboard"
              data-testid="boost-confirm-done-link"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-full px-7 py-3 transition-colors shadow-sm"
            >
              Back to leaderboard
            </Link>
          </motion.div>
        )}

        {/* Payment confirmed — new checkout flow success */}
        {!loading && !error && sub && !done && sub.status === "pending_review" && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="boost-payment-confirmed"
            className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-3">Payment confirmed</h1>
            <p className="text-sm text-slate-600 max-w-md mx-auto mb-8 leading-relaxed">
              Your {TIER_LABELS[sub.tier]} content for <span className="text-slate-900 font-semibold">{sub.business_name}</span> is
              now with our team. We review within 24 hours and email you the moment it goes live.
            </p>
            <Link
              to="/leaderboard"
              data-testid="boost-confirmed-done-link"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-full px-7 py-3 transition-colors shadow-sm"
            >
              Back to leaderboard
            </Link>
          </motion.div>
        )}

        {/* Confirming payment — waiting for Stripe */}
        {!loading && !error && sub && !done && sub.status === "pending_payment" && (
          <div data-testid="boost-payment-processing" className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
              <Loader2 size={28} className="text-amber-600 animate-spin" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-2">Confirming payment</h1>
            <p className="text-sm text-slate-600 max-w-md mx-auto mb-6 leading-relaxed">
              We're confirming your payment with Stripe — this usually takes a few seconds.
            </p>
            <button
              onClick={loadSubmission}
              data-testid="boost-payment-retry"
              className="inline-flex items-center gap-2 border border-amber-300 bg-white hover:bg-amber-50 text-amber-700 font-bold text-sm rounded-full px-7 py-3 transition-colors shadow-sm"
            >
              Check again
            </button>
            <div className="flex items-center justify-center gap-1.5 mt-5 opacity-40">
              <Lock size={10} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 tracking-wide">Payments powered by</span>
              <svg viewBox="0 0 512 214" className="h-[14px] w-auto" aria-label="Stripe">
                <path fill="#635bff" d="M512 110.08c0-36.409-17.636-65.138-51.342-65.138-33.85 0-54.33 28.73-54.33 64.854 0 42.808 24.179 64.426 58.88 64.426 16.925 0 29.725-3.84 39.396-9.244v-28.445c-9.67 4.836-20.764 7.823-34.844 7.823-13.796 0-26.027-4.836-27.591-21.618h69.547c0-1.85.284-9.245.284-12.658m-70.258-13.511c0-16.071 9.814-22.756 18.774-22.756 8.675 0 17.92 6.685 17.92 22.756zm-90.31-51.627c-13.939 0-22.899 6.542-27.876 11.094l-1.85-8.818h-31.288v165.83l35.555-7.537.143-40.249c5.12 3.698 12.657 8.96 25.173 8.96 25.458 0 48.64-20.48 48.64-65.564-.142-41.245-23.609-63.716-48.498-63.716m-8.534 97.991c-8.391 0-13.37-2.986-16.782-6.684l-.143-52.765c3.698-4.124 8.818-6.968 16.925-6.968 12.942 0 21.902 14.506 21.902 33.137 0 19.058-8.818 33.28-21.902 33.28M241.493 36.551l35.698-7.68V0l-35.698 7.538zm0 10.809h35.698v124.444h-35.698zm-38.257 10.524L200.96 47.36h-30.72v124.444h35.556V87.467c8.39-10.951 22.613-8.96 27.022-7.396V47.36c-4.551-1.707-21.191-4.836-29.582 10.524m-71.112-41.386-34.702 7.395-.142 113.92c0 21.05 15.787 36.551 36.836 36.551 11.662 0 20.195-2.133 24.888-4.693V140.8c-4.55 1.849-27.022 8.391-27.022-12.658V77.653h27.022V47.36h-27.022zM35.982 83.484c0-5.546 4.551-7.68 12.09-7.68 10.808 0 24.461 3.272 35.27 9.103V51.484c-11.804-4.693-23.466-6.542-35.27-6.542C19.2 44.942 0 60.018 0 85.192c0 39.252 54.044 32.995 54.044 49.92 0 6.541-5.688 8.675-13.653 8.675-11.804 0-26.88-4.836-38.827-11.378v33.849c13.227 5.689 26.596 8.106 38.827 8.106 29.582 0 49.92-14.648 49.92-40.106-.142-42.382-54.329-34.845-54.329-50.774" />
              </svg>
            </div>
          </div>
        )}

        {/* Already submitted — catch-all for other statuses */}
        {!loading && !error && sub && !done && !["awaiting_content", "pending_review", "pending_payment"].includes(sub.status) && (
          <div data-testid="boost-content-already-submitted" className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} className="text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-2">Already submitted</h1>
            <p className="text-sm text-slate-500">
              We've already got the content for this {TIER_LABELS[sub.tier]} payment — it's in review.
            </p>
          </div>
        )}

        {/* Awaiting content — legacy Payment Links flow */}
        {!loading && !error && sub && !done && sub.status === "awaiting_content" && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <div
              data-testid="boost-payment-received"
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 mb-6"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={22} className="text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h1 className="font-display text-2xl uppercase tracking-wide text-slate-900 mb-1">Payment received</h1>
                  <p className="text-sm text-slate-600">
                    {TIER_LABELS[sub.tier]} · {money(sub.amount_paid, sub.currency)} · {sub.business_name}
                  </p>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    Please submit your video / social link for review below. Reviewed within 24 hours — content that
                    doesn't meet our guidelines is refunded in full.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6 shadow-sm">
              {sub.match_status === "unmatched" && (
                <div data-testid="boost-confirm-link-listing" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-xs text-amber-800 mb-3 leading-relaxed">
                    We couldn't match this payment to a listing automatically. Pick your listing, or leave it blank and our
                    team will link it for you.
                  </p>
                  <select
                    data-testid="boost-confirm-listing-select"
                    value={listingId}
                    onChange={(e) => setListingId(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                  >
                    <option value="">Let the team link it</option>
                    {myListings.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {sub.tier === "discover_boost" && (
                <div>
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-3">Choose your option</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { id: "option_a", title: "Option A", desc: "Sponsored Discover page slot" },
                      { id: "option_b", title: "Option B", desc: "Leaderboard video placement" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`boost-confirm-${opt.id}`}
                        onClick={() => setOption(opt.id)}
                        className={`text-left rounded-xl border p-4 transition-all ${
                          option === opt.id
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <p className="text-sm font-bold text-slate-800">{opt.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-2">
                  Social Post Link {sub.tier === "discover_boost" && option === "option_b" ? "(optional)" : "(required)"}
                </label>
                <div className="relative">
                  <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="boost-confirm-social-link"
                    value={socialLink}
                    onChange={(e) => setSocialLink(e.target.value)}
                    placeholder="https://x.com/yourbrand/status/..."
                    className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                  />
                </div>
              </div>

              {(sub.tier === "boost" || sub.tier === "pro_boost") && (
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-2">
                    Second Social Post Link (optional)
                  </label>
                  <div className="relative">
                    <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="boost-confirm-social-link-2"
                      value={socialLink2}
                      onChange={(e) => setSocialLink2(e.target.value)}
                      placeholder="https://reddit.com/r/..."
                      className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-2">
                  Upload Video — {VIDEO_LABELS[sub.tier]} {needsVideo ? "(required)" : "(optional)"}
                </label>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                />
                {videoFile ? (
                  <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <span data-testid="boost-confirm-video-name" className="text-sm text-slate-700 truncate flex-1">
                      {videoFile.name}
                    </span>
                    <button type="button" onClick={() => setVideoFile(null)} className="text-slate-400 hover:text-slate-600 transition">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="boost-confirm-video-upload"
                    onClick={() => videoInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:border-emerald-400 px-4 py-6 text-sm text-slate-500 transition"
                  >
                    <Upload size={15} /> Choose a video (max 15 MB)
                  </button>
                )}
              </div>

              <button
                type="submit"
                data-testid="boost-confirm-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold text-sm rounded-full px-7 py-3.5 transition-colors shadow-sm"
              >
                {submitting ? <><Loader2 size={15} className="animate-spin" /> Submitting…</> : "Submit for review"}
              </button>
            </form>
          </motion.div>
        )}
      </section>
    </main>
  );
}
